import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { 
  ArrowRight, 
  BookOpen, 
  Lock, 
  Sparkles,
  Target,
  Users,
  TrendingUp,
  FileText,
  GraduationCap,
  CheckCircle2,
  Clock,
  Briefcase,
  BarChart3,
  Database,
  Brain,
  Monitor,
  Server,
  Cloud,
  HeartHandshake,
  DollarSign,
  MessageCircle,
  Bell,
  BellRing,
  type LucideIcon
} from "lucide-react";
import { useTrackInterests } from "@/hooks/useTrackInterests";
import { toast } from "sonner";
import { useCourseApplications } from "@/hooks/useCourses";

const cicloBasico = {
  id: "basico",
  titulo: "Ciclo Básico",
  descricao: "Fundamentos para carreiras digitais",
  duracao: "4 semanas",
  modulos: [
    { nome: "Fundamentos de Produto", icon: Target },
    { nome: "UX & Pesquisa", icon: Users },
    { nome: "Negócios & Growth", icon: TrendingUp },
  ],
  locked: false,
};

const trilhas: { id: string; titulo: string; descricao: string; duracao: string; icon: LucideIcon }[] = [
  { id: "produto", titulo: "Gestão de Produto", descricao: "Aprofundamento em estratégia, discovery e delivery de produto", duracao: "8 semanas", icon: Target },
  { id: "ux-design", titulo: "UX e Design de Produto", descricao: "Pesquisa, prototipação e design de interfaces", duracao: "8 semanas", icon: Users },
  { id: "dados-bi", titulo: "Análise de Dados, SQL e BI", descricao: "SQL, dashboards e tomada de decisão baseada em dados", duracao: "8 semanas", icon: BarChart3 },
  { id: "eng-dados", titulo: "Engenharia de Dados", descricao: "Pipelines, ETL e infraestrutura de dados", duracao: "10 semanas", icon: Database },
  { id: "ciencia-dados", titulo: "Ciência de Dados", descricao: "Modelagem estatística, machine learning e experimentação", duracao: "10 semanas", icon: Brain },
  { id: "marketing", titulo: "Marketing Digital, Growth e Performance", descricao: "Aquisição, retenção, SEO e mídia paga", duracao: "6 semanas", icon: TrendingUp },
  { id: "frontend", titulo: "Desenvolvimento Front-end", descricao: "HTML, CSS, JavaScript, React e interfaces responsivas", duracao: "10 semanas", icon: Monitor },
  { id: "backend", titulo: "Desenvolvimento Back-end", descricao: "APIs, bancos de dados e arquitetura de sistemas", duracao: "10 semanas", icon: Server },
  { id: "devops", titulo: "Infraestrutura e DevOps", descricao: "Cloud, CI/CD, containers e observabilidade", duracao: "8 semanas", icon: Cloud },
  { id: "cs-cx", titulo: "Customer Success e CX", descricao: "Jornada do cliente, onboarding e retenção", duracao: "6 semanas", icon: HeartHandshake },
  { id: "revops", titulo: "Operações de Vendas e Receita (RevOps)", descricao: "Processos comerciais, CRM e métricas de receita", duracao: "6 semanas", icon: DollarSign },
  { id: "comunidades", titulo: "Gestão de Comunidades, Social Media e Conteúdo", descricao: "Engajamento, produção de conteúdo e redes sociais", duracao: "6 semanas", icon: MessageCircle },
];

const STATUS_MAP: Record<string, { label: string; color: string; icon: typeof CheckCircle2 }> = {
  applied: { label: "Em análise", color: "text-accent", icon: Clock },
  accepted: { label: "Aceito", color: "text-success", icon: CheckCircle2 },
  rejected: { label: "Não aprovado", color: "text-destructive", icon: Clock },
  waitlist: { label: "Lista de espera", color: "text-muted-foreground", icon: Clock },
  completed: { label: "Concluído", color: "text-success", icon: CheckCircle2 },
  cancelled: { label: "Cancelado", color: "text-muted-foreground", icon: Clock },
};

export default function AlunoAprender() {
  const navigate = useNavigate();
  const { applications, isLoading } = useCourseApplications();
  const { interests, toggleInterest, isToggling } = useTrackInterests();

  const hasApplications = applications.length > 0;

  return (
    <div className="page-container">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <h1 className="font-heading text-2xl font-bold text-foreground">Aprender</h1>
        <p className="text-muted-foreground text-sm mt-1">Evolua com trilhas práticas</p>
      </motion.div>

      <Tabs defaultValue={hasApplications ? "meus-cursos" : "explorar"} className="w-full">
        <TabsList className="w-full mb-6">
          <TabsTrigger value="explorar" className="flex-1">Explorar</TabsTrigger>
          <TabsTrigger value="meus-cursos" className="flex-1">
            Meus Cursos
            {hasApplications && (
              <span className="ml-1.5 bg-primary text-primary-foreground text-[10px] rounded-full w-5 h-5 inline-flex items-center justify-center">
                {applications.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* === EXPLORAR === */}
        <TabsContent value="explorar" className="space-y-6">
          {/* Funnel Visual */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-primary rounded-2xl p-5 text-primary-foreground">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-primary-foreground/20 flex items-center justify-center">
                <BookOpen className="w-6 h-6" />
              </div>
              <div>
                <p className="font-heading font-semibold text-lg">Como funciona</p>
                <p className="text-sm opacity-80">Sua jornada de aprendizado</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="bg-primary-foreground/20 px-3 py-1 rounded-full">1. Ciclo Básico</span>
              <ArrowRight className="w-4 h-4 opacity-60" />
              <span className="opacity-60">2. Trilhas Especialistas</span>
            </div>
          </motion.div>

          {/* Ciclo Básico */}
          <section>
            <h2 className="font-heading text-base font-semibold text-foreground mb-3">Ciclo Básico</h2>
            <div className="bg-card rounded-2xl p-5 border-2 border-primary shadow-card">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center shrink-0">
                  <BookOpen className="w-6 h-6 text-success" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-heading font-semibold text-foreground">{cicloBasico.titulo}</h3>
                    <Badge variant="ready" className="text-[10px]">Disponível</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{cicloBasico.descricao}</p>
                  <p className="text-xs text-muted-foreground mt-1">{cicloBasico.duracao}</p>
                </div>
              </div>
              <div className="space-y-2 mb-4">
                {cicloBasico.modulos.map((modulo, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-secondary rounded-xl">
                    <modulo.icon className="w-4 h-4 text-primary" />
                    <span className="text-sm text-foreground">{modulo.nome}</span>
                  </div>
                ))}
              </div>
              <div className="bg-accent/10 rounded-xl p-4 mb-4 border border-accent/20">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Saia com um case de portfólio</p>
                    <p className="text-xs text-muted-foreground mt-1">Aprenda fazendo. Tenha evidência real para entrevistas, mesmo sem experiência formal.</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
                <FileText className="w-4 h-4" />
                <span>Entregáveis: Case, Protótipo, Análises, Proposta de Valor</span>
              </div>
              <Button variant="hero" size="lg" className="w-full" onClick={() => navigate("/aluno/aprender/aplicar/basico")}>
                Aplicar para curso
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </section>

          {/* Trilhas Especialistas */}
          <section>
            <h2 className="font-heading text-base font-semibold text-foreground mb-3">Trilhas Especialistas</h2>
            <div className="space-y-3">
              {trilhas.map((trilha) => {
                const TrilhaIcon = trilha.icon;
                const hasInterest = interests.includes(trilha.id);
                return (
                  <div key={trilha.id} className={`bg-card rounded-2xl p-4 border ${hasInterest ? "border-primary/40" : "border-border"} ${hasInterest ? "opacity-100" : "opacity-60"} transition-all`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl ${hasInterest ? "bg-primary/10" : "bg-muted"} flex items-center justify-center shrink-0 relative`}>
                        <TrilhaIcon className={`w-5 h-5 ${hasInterest ? "text-primary" : "text-muted-foreground"}`} />
                        <Lock className="w-3 h-3 text-muted-foreground absolute -bottom-0.5 -right-0.5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-medium text-foreground text-sm">{trilha.titulo}</h3>
                          <Badge variant="outline" className="text-[10px]">Após o Básico</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{trilha.descricao}</p>
                        <p className="text-xs text-muted-foreground mt-1">{trilha.duracao}</p>
                      </div>
                    </div>
                    <Button
                      variant={hasInterest ? "default" : "outline"}
                      size="sm"
                      className="w-full mt-3"
                      disabled={isToggling}
                      onClick={async () => {
                        try {
                          const result = await toggleInterest(trilha.id);
                          toast.success(result.added ? "Interesse marcado! Você será notificado quando a trilha abrir." : "Interesse removido.");
                        } catch {
                          toast.error("Erro ao atualizar interesse.");
                        }
                      }}
                    >
                      {hasInterest ? <BellRing className="w-4 h-4 mr-1" /> : <Bell className="w-4 h-4 mr-1" />}
                      {hasInterest ? "Interesse marcado" : "Tenho interesse"}
                    </Button>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground text-center mt-4">
              Complete o Ciclo Básico para desbloquear as trilhas especialistas
            </p>
          </section>
        </TabsContent>

        {/* === MEUS CURSOS === */}
        <TabsContent value="meus-cursos">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : applications.length === 0 ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="text-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="font-heading font-semibold text-foreground mb-1">Nenhum curso ainda</h3>
              <p className="text-sm text-muted-foreground mb-6">Explore os cursos disponíveis e aplique!</p>
              <Button variant="outline" onClick={() => {
                const tabEl = document.querySelector('[data-state="inactive"][value="explorar"]') as HTMLElement;
                tabEl?.click();
              }}>
                Explorar cursos
              </Button>
            </motion.div>
          ) : (
            <div className="space-y-4">
              {applications.map((app) => {
                const status = STATUS_MAP[app.status] || STATUS_MAP.applied;
                const StatusIcon = status.icon;
                const isAccepted = app.status === "accepted";
                const course = app.courses;

                return (
                  <motion.div key={app.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    className={`bg-card rounded-2xl p-5 border ${isAccepted ? "border-success/40" : "border-border"} shadow-card`}>
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`w-12 h-12 rounded-xl ${isAccepted ? "bg-success/10" : "bg-muted"} flex items-center justify-center shrink-0`}>
                        <GraduationCap className={`w-6 h-6 ${isAccepted ? "text-success" : "text-muted-foreground"}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-heading font-semibold text-foreground">
                            {course?.title || "Curso"}
                          </h3>
                        </div>
                        <p className="text-sm text-muted-foreground">{course?.description || ""}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mb-3">
                      <StatusIcon className={`w-4 h-4 ${status.color}`} />
                      <span className={`text-sm font-medium ${status.color}`}>{status.label}</span>
                    </div>

                    {isAccepted && (
                      <div className="bg-success/5 rounded-xl p-4 border border-success/20">
                        <p className="text-sm font-medium text-foreground mb-1">🎉 Parabéns! Você foi aceito!</p>
                        <p className="text-xs text-muted-foreground">
                          Nossa equipe entrará em contato em breve com os próximos passos para iniciar o curso. Fique atento ao seu e-mail e WhatsApp.
                        </p>
                      </div>
                    )}

                    {app.status === "applied" && (
                      <div className="bg-accent/5 rounded-xl p-4 border border-accent/20">
                        <p className="text-sm font-medium text-foreground mb-1">Aplicação em análise</p>
                        <p className="text-xs text-muted-foreground">
                          Estamos avaliando seu perfil. Você receberá uma resposta em breve.
                        </p>
                      </div>
                    )}

                    {app.status === "rejected" && (
                      <div className="bg-destructive/5 rounded-xl p-4 border border-destructive/20">
                        <p className="text-sm font-medium text-foreground mb-1">Aplicação não aprovada</p>
                        <p className="text-xs text-muted-foreground">
                          Continue evoluindo seu perfil. Você poderá aplicar novamente no futuro.
                        </p>
                      </div>
                    )}

                    {isAccepted && (
                      <Button variant="outline" className="w-full mt-3" onClick={() => navigate("/aluno/vagas")}>
                        <Briefcase className="w-4 h-4 mr-2" />Ver Vagas de Entrada
                      </Button>
                    )}

                    <p className="text-xs text-muted-foreground mt-3">
                      Aplicado em {new Date(app.created_at).toLocaleDateString("pt-BR")}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
